"use client";
import React from "react";
import { Trash2 } from "lucide-react";
import MedicationField from "./MedicationField.jsx";
import styles from "../css/index.js";
import DrugSearchInputClean from "../../../Inventory Managment/Recieve Stock/components/DrugSearchInputClean.jsx";
import { searchPatients } from "../../../../api/dispensary/script/LabelApi.js";
import Button from "../../../../components/Button.jsx";

export default function MedicationFormCardView({
  form,
  index,
  total,
  onRemove,
  onFieldChange,
  onTogglePreview,
  onFetchDrugInfo,
  isPreviewActive,
  prescriptionId,
  isFromDispensaryMenu,

  directionsOptions,
  formatPatientNameForDisplay,

  rxNumbers,
  selectedPatient,
  medications,
  selectedRxNumber,
  icd10Codes,
  selectedMedication,
  isMedicationInItemTable,

  setSelectedPatient,
  setSelectedRxNumber,
  setSelectedMedication,
  setIcd10Codes,
  setIsMedicationInItemTable,

  handlePrint,
  showRepeat,
}) {
  return (
    <div className={`${styles.formCard} ${styles.formCardAuto}`} data-form-id={form.id}>
      <div className={styles.cardHeader}>
        <span className={styles.cardNumber}>#{index + 1}</span>

        {total > 1 && (
          <button
            className={styles.removeButton}
            onClick={() => onRemove(form.id)}
            title="Delete Medication"
          >
            <Trash2 size={16} color="#e53e3e" />
          </button>
        )}
      </div>

      <div className={styles.formGrid}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Patient Name</label>

          <DrugSearchInputClean
            key={`patient-search-${form.id}`}
            id={`patient-${form.id}`}
            initialValue={formatPatientNameForDisplay(form.data.patientName)}
            placeholder="Search patient name..."
            size="small"
            noResultsMessage="No patient found"
            onSelect={(patient) => {
              setSelectedPatient(patient);

              // Auto-fill practitioner field if practitioner information is available

              if (patient && patient.practitionerName) {
                onFieldChange(form.id, "practitioner", patient.practitionerName);
              }

              onFieldChange(form.id, "patientName", patient.name || patient);
            }}
            onInputChange={(term) => onFieldChange(form.id, "patientName", term)}
            searchBackend={async (term) => {
              try {
                const response = await searchPatients(term);
                return response.data || [];
              } catch (error) {
                console.error("Error searching patients:", error);
                return [];
              }
            }}
            getDisplayValue={(patient) => patient?.name || patient}
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>RX Number</label>

          {prescriptionId ? (
            // Prescription workflow: use dropdown

            <select
              className={styles.select}
              value={form.data.rxNumber}
              onChange={(e) => {
                const selectedRx = rxNumbers.find(
                  (rx) => rx.displayValue === e.target.value
                );

                setSelectedRxNumber(selectedRx);

                // Auto-fill prescriber field if pharmacist information is available

                if (selectedRx && selectedRx.pharmacistName) {
                  onFieldChange(form.id, "prescriber", selectedRx.pharmacistName);
                }

                onFieldChange(form.id, "rxNumber", e.target.value);
              }}
            >
              <option value="">
                {selectedPatient ? "Select RX Number" : "Select Patient First"}
              </option>

              {console.log(
                "Rendering dropdown with rxNumbers:",
                rxNumbers,
                "current value:",
                form.data.rxNumber
              )}

              {rxNumbers.map((rx) => (
                <option key={rx.id} value={rx.displayValue}>
                  {rx.displayValue}
                </option>
              ))}
            </select>
          ) : (
            // Normal flow from dispensary menu: use dropdown with all script numbers

            <select
              className={styles.select}
              value={form.data.rxNumber}
              onChange={(e) => {
                const selectedRx = rxNumbers.find(
                  (rx) =>
                    (rx.processScriptNumber || rx.displayValue || rx.name || rx) ===
                    e.target.value
                );

                setSelectedRxNumber(selectedRx);

                // Auto-fill prescriber field if pharmacist information is available

                if (selectedRx && selectedRx.pharmacistName) {
                  onFieldChange(form.id, "prescriber", selectedRx.pharmacistName);
                }

                onFieldChange(form.id, "rxNumber", e.target.value);
              }}
              disabled={!selectedPatient}
            >
              <option value="">
                {selectedPatient ? "Select RX Number" : "Select Patient First"}
              </option>

              {console.log(
                "Rendering dropdown with rxNumbers:",
                rxNumbers,
                "current value:",
                form.data.rxNumber
              )}

              {rxNumbers.map((rx) => (
                <option
                  key={rx.id}
                  value={rx.processScriptNumber || rx.displayValue || rx.name || rx}
                >
                  {rx.processScriptNumber || rx.displayValue || rx.name || rx}
                </option>
              ))}
            </select>
          )}
        </div>

        <MedicationField
          form={form}
          medications={medications}
          selectedRxNumber={selectedRxNumber}
          selectedMedication={selectedMedication}
          setSelectedMedication={setSelectedMedication}
          setIcd10Codes={setIcd10Codes}
          setIsMedicationInItemTable={setIsMedicationInItemTable}
          onFieldChange={onFieldChange}
          prescriptionId={prescriptionId}
        />

        <div className={styles.inputGroup}>
          <label className={styles.label}>Date</label>

          <input
            type="date"
            value={form.data.date}
            onChange={(e) => onFieldChange(form.id, "date", e.target.value)}
            className={styles.input}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Quantity</label>

          <div className={styles.quantityWrapper}>
            <input
              type="text"
              value={form.data.quantity}
              onChange={(e) => onFieldChange(form.id, "quantity", e.target.value)}
              className={styles.quantityInput}
            />

            <div className={styles.quantityControls}>
              <Button
                className={styles.quantityBtn}
                variant="primary"
                onClick={() =>
                  onFieldChange(
                    form.id,
                    "quantity",
                    String(Number(form.data.quantity || 0) + 1)
                  )
                }
              >
                ▲
              </Button>

              <Button
                className={styles.quantityBtn}
                variant="primary"
                onClick={() =>
                  onFieldChange(
                    form.id,
                    "quantity",
                    String(Math.max(Number(form.data.quantity || 0) - 1, 0))
                  )
                }
              >
                ▼
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Pack Size</label>

          <input
            type="text"
            value={form.data.packSize}
            onChange={(e) => onFieldChange(form.id, "packSize", e.target.value)}
            className={styles.input}
          />
        </div>

        <div
          className={`${styles.inputGroup} ${
            isMedicationInItemTable ? styles.hiddenVisibility : styles.visibleVisibility
          }`}
        >
          {console.log("ICD-10 visibility check:", {
            medication: form.data.medication,
            isMedicationInItemTable,
            shouldHide: isMedicationInItemTable,
          })}

          <label className={styles.label}>ICD-10</label>

          {prescriptionId ? (
            // Auto-population mode: show as read-only input

            <input
              type="text"
              value={form.data.icd10}
              readOnly
              className={`${styles.input} ${
                isMedicationInItemTable
                  ? styles.hiddenVisibility
                  : styles.visibleVisibility
              }`}
              placeholder="ICD-10 codes"
            />
          ) : (
            // Manual mode: show as dropdown

            <select
              className={`${styles.select} ${
                isMedicationInItemTable
                  ? styles.hiddenVisibility
                  : styles.visibleVisibility
              }`}
              value={form.data.icd10}
              onChange={(e) => onFieldChange(form.id, "icd10", e.target.value)}
            >
              <option value="">
                {selectedMedication
                  ? "Select ICD-10 Code"
                  : "Select Medication First"}
              </option>

              {/* Use icd10Codes state for dropdown options */}

              {icd10Codes.map((code, idx) => (
                <option key={idx} value={code}>
                  {code}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Prescriber</label>

          {prescriptionId ? (
            // Auto-population mode: show as read-only input with pharmacist name

            <input
              type="text"
              value={form.data.prescriber}
              readOnly
              className={styles.input}
              placeholder="Pharmacist Name"
            />
          ) : isFromDispensaryMenu ? (
            // From dispensary menu: show as read-only input with logged-in user

            <input
              type="text"
              value={form.data.prescriber}
              readOnly
              className={styles.input}
              placeholder="Logged-in User"
            />
          ) : (
            // Manual mode: show as dropdown

            <select
              className={styles.select}
              value={form.data.prescriber}
              onChange={(e) => onFieldChange(form.id, "prescriber", e.target.value)}
            >
              <option value={form.data.prescriber}>
                {form.data.prescriber || "Select Prescriber"}
              </option>
            </select>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Practitioner</label>

          <input
            type="text"
            value={
              typeof form.data.practitioner === "object" && form.data.practitioner
                ? `${form.data.practitioner.title || ""} ${
                    form.data.practitioner.firstname || ""
                  } ${form.data.practitioner.surname || ""}`.trim()
                : form.data.practitioner || ""
            }
            onChange={(e) => onFieldChange(form.id, "practitioner", e.target.value)}
            className={styles.input}
          />
        </div>

        <div className={`${styles.inputGroup} ${styles.fullColumnSpan}`}>
          <label className={styles.label}>Directions</label>

          {prescriptionId ? (
            // Auto-population mode: show as read-only input with prescription directions

            <input
              type="text"
              value={form.data.directions}
              readOnly
              className={styles.input}
              placeholder="Prescription Directions"
            />
          ) : (
            // Manual mode: show as dropdown

            <>
              <select
                className={styles.select}
                value={form.data.directions}
                onChange={(e) => onFieldChange(form.id, "directions", e.target.value)}
              >
                {directionsOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {form.data.directions === "Other" && (
                <input
                  type="text"
                  value={form.data.directionsOther || ""}
                  onChange={(e) =>
                    onFieldChange(form.id, "directionsOther", e.target.value)
                  }
                  className={`${styles.input} ${styles.directionsOtherMargin}`}
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className={styles.actionButtons}>
        <Button
          className={styles.previewButton}
          onClick={() => {
            onTogglePreview(form.id);
            // Fetch drug info when preview is opened
            if (!isPreviewActive) {
              onFetchDrugInfo?.(form.id);
            }
          }}
          variant="primary"
        >
          {isPreviewActive ? "Hide Preview" : "Preview"}
        </Button>

        <Button
          className={styles.printButton}
          variant="primary"
          onClick={() => handlePrint(form.id, showRepeat)}
        >
          Print
        </Button>
      </div>
    </div>
  );
}
